import { filter, forEach, get, head, keys, map, orderBy } from 'lodash';
import { differenceInDays, differenceInYears, parse } from 'date-fns';
import { IMtaFormNames, IMtaState } from '../../../../../../../services/mta/models';
import { ICustomerMTAOptions } from '../../../index';
import { IAuthState } from '../../../../../../../services/auth/models';
import { IPolicyApplication, IProduct } from '../../../../../../../data/api/models';
import { isLuckyTripProduct } from '../../../../../../../helpers/TravelApplication';
import Heap from '../../../../../../../helpers/Heap';
import { MtaSelection } from '../index';
import { IOptionSelection } from '../../../../../../../services/quote/models';

export const canUpsellPolicy = (selectedPolicy: any, mta: IMtaState, customerMTAOptions: ICustomerMTAOptions): boolean => {
  const isSingle = get(selectedPolicy, 'metadata.scheme_type', '') === 'single';
  const isPolicyNotExpired = new Date() < parse(get(mta, 'selectedPolicy.expiresAt', get(selectedPolicy, 'expiresAt')), 'dd/MM/yyyy', new Date())
  const selectedPolicyMeta = get(selectedPolicy, 'metadata');
  const tripDuration = differenceInDays(new Date(get(selectedPolicyMeta, 'end_date')), new Date(get(selectedPolicyMeta, 'start_date')))
  const upsellMaxTripDuration = get(mta, 'policyInfo.meta.upsellMaxTripDuration', 30)
  const canShowUpsellOption = get(customerMTAOptions, 'change_trip_type.enabled', false) && tripDuration <= upsellMaxTripDuration
  const isDisabledByCampaign = selectedPolicy.campaigns?.some(camp => camp.metadata?.prevent_mta_upsell);

  return canShowUpsellOption && isSingle && isPolicyNotExpired && !isDisabledByCampaign;
}

export const canCancel = (customerMTAOptions: ICustomerMTAOptions, isRenewal = false) => {
  return isRenewal ?
    get(customerMTAOptions, 'allow_cancel_renewal.enabled', false) :
    get(customerMTAOptions, 'allow_cancel_policy.enabled', false);
}

export const findPolicyRenewal = (auth: IAuthState, mta: IMtaState) => {
  const policyId = get(mta, 'selectedPolicy.id');
  const renewal = head(orderBy(filter(get(auth, 'customer.renewals'), ['renewingPolicyId', policyId]), ['version'], ['desc']));
  const statuses = ['renewal', 'renewal invited']
  if (statuses.includes(renewal?.status)) {
    return renewal;
  }
  return null;
}

export const resetForms = (actions: any) => {
  forEach(IMtaFormNames, (value) => {
    actions.reset(value);
    actions.destroy(value);
  });
  actions.mta.resetMtaData();
}

export const setSelectedPolicy = ({
                                    policyId,
                                    auth,
                                    actions,
                                    mta,
                                    callback,
                                    customerMTAOptions,
                                    track = false,
                                    checkWSExclusion = false
                                  }): void => {
  let selectedPolicy = null;
  if (get(auth, 'customer.activePolicies', []).length) {

    forEach(auth.customer.activePolicies, (policySelected) => {
      if (policySelected.id === policyId) {
        selectedPolicy = policySelected;
      }
    });

    if (selectedPolicy && !isLuckyTripProduct(selectedPolicy)) {
      Promise.resolve(actions.mta.setSelectedPolicy(selectedPolicy)).then(() => {
        Promise.resolve(actions.mta.getPolicyInfo()).then(() => {
          const canUpsell = canUpsellPolicy(selectedPolicy, mta, customerMTAOptions);
          Promise.resolve(actions.mta.setUpsellRules(canUpsell)).then(() => {
            if (track) {
              Heap.trackActivePolicyEvent(
                'MtaJourneyStart',
                selectedPolicy,
                get(auth, 'customer.marketingOptions'),
                actions.paymentMethod.getPolicyPaymentMethod(get(selectedPolicy, 'assignedPaymentMethods'))
              );
            }

            if (checkWSExclusion) {
              Promise.resolve(actions.mta.checkIfHasWSExclusion()).then(() => {
                callback();
              })
            } else {
              callback();
            }
          })
        });
      });
      return;
    } else {
      actions.push(`/account/policies/${policyId}`);
    }

  }
}

export const handleClick = (
  policyId: string,
  actions: any,
  selection: MtaSelection,
  renewal: IPolicyApplication = null,
  optionKey: string = null,
  disabled: boolean = false
) => () => {
  switch (selection) {
    case MtaSelection.PERSONAL_DETAILS:
      resetForms(actions);
      actions.push(`/account/make-a-change/personal-details/${policyId}`);
      break;
    case MtaSelection.ADDITIONAL_OPTION:
    case MtaSelection.REMOVE_OPTION:
    case MtaSelection.ADD_TRAVELLER:
    case MtaSelection.ADD_MEDICAL:
    case MtaSelection.REMOVE_TRAVELLER:
    case MtaSelection.CHANGE_REGION:
    case MtaSelection.CHANGE_TRIP_DURATION:
    case MtaSelection.CHANGE_TIER:
    case MtaSelection.CHANGE_DESTINATION:
    case MtaSelection.CHANGE_TRIP_DATE:
      resetForms(actions);
      if (disabled) {
        actions.push(`/account/make-a-change/contact`)
        break;
      }
      actions.mta.setSelectedMTAChange(selection)
      if (optionKey) {
        actions.mta.setSelectedOptionToAdd(optionKey)
      }
      actions.push(`/account/make-a-change/cover-details/${policyId}`);
      break;
    case MtaSelection.AMT_UPSELL:
      resetForms(actions);
      actions.push(`/account/make-a-change/amt-upsell`);
      break;
    case MtaSelection.CANCEL_POLICY:
      resetForms(actions);
      actions.push(`/account/make-a-change/cancel-policy`);
      break;
    case MtaSelection.CANCEL_RENEWAL:
      resetForms(actions);
      actions.quote.initRenewal(renewal, `/account/make-a-change/cancel-renewal`);
      break;
  }
}

export const checkIfAllOptionsAreAdded = (mta: IMtaState) => {
  const mtaOptions = get(mta, 'selectedPolicy.metadata.options');
  const leadTraveller = get(mta, 'selectedPolicy.metadata.adults[0]', null);
  let excludeWinterSports = false;
  if (leadTraveller) {
    const leadTravellerAge = differenceInYears(new Date(), leadTraveller.dob);
    if (leadTravellerAge > 65) {
      excludeWinterSports = true;
    }
  }

  const allOptionsAdded = !!(mtaOptions && mtaOptions.winter_sports && mtaOptions.gadget_cover
    && mtaOptions.cruise_cover && mtaOptions.excess_waiver);
  const allOptionsExcludeWinter = (mtaOptions && mtaOptions.gadget_cover
    && mtaOptions.cruise_cover && mtaOptions.excess_waiver) && excludeWinterSports;

  return (allOptionsAdded || allOptionsExcludeWinter);
}

export const alreadyAddedOptions = (mta: IMtaState, callback = null): string[] => {
  const addedOptions = [];
  const mtaOptions = get(mta, 'selectedPolicy.metadata.options');

  const allOptions = ['winter_sports', 'cruise_cover', 'gadget_cover', 'excess_waiver', 'enhanced_trip_disruption'];
  allOptions.forEach((key) => {
    if (!get(mtaOptions, key)) {

      if (callback) {
        callback(key)
      }
    }
    if (get(mtaOptions, key)) {
      addedOptions.push(key);
    }
  });

  return addedOptions
}

export const winterSportsRule = (key: string, startDate: Date, endDate: Date) => {
  const MAX_DAYS_WINTER = 21;
  const duration = differenceInDays(endDate, startDate);
  return !(duration > MAX_DAYS_WINTER && key === 'winter_sports');
}

export const setMTATripDuration = (
  product: IProduct,
  allowedMaxTripDuration: number,
  mta: IMtaState,
  change: any,
  dispatch: any
) => {
  const tripDuration = differenceInDays(new Date(get(mta, 'selectedPolicy.metadata.end_date')), new Date(get(mta, 'selectedPolicy.metadata.start_date')))
  const maxTripDurations = keys(get(product, 'metadata.annual_trip_durations', []))
  let numberMaxTripDurations = [];
  if (maxTripDurations) {
    numberMaxTripDurations = maxTripDurations.map(Number)
  }
  let AMTTripDuration = get(numberMaxTripDurations, '0', 31);

  if (allowedMaxTripDuration === get(numberMaxTripDurations, '2', 60)) {
    if (tripDuration >= 32 && tripDuration <= 45) {
      AMTTripDuration = get(numberMaxTripDurations, '1', 45);
    }
    if (tripDuration >= 46 && tripDuration <= 60) {
      AMTTripDuration = get(numberMaxTripDurations, '2', 60);
    }
  }
  dispatch(change('data.attributes.metadata.max_trip_duration', AMTTripDuration))
}

export const getAnnualTripDurationOptions = (product: IProduct): IOptionSelection[] => {
  return map(get(product, 'metadata.annual_trip_durations', {}), (name, key): IOptionSelection => {
    return {
      label: name,
      value: parseInt(key, 10),
    };
  });
}
